import { Box, Flex, Text } from '@chakra-ui/react';
import { Accordion, Button, Modal, OutlineCard } from '@morressier/alembic';
import { useForm } from 'react-hook-form';
import { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FieldLabel } from 'src/components/Field';

import {
  FieldEditorSubmit,
  FieldFormValues,
  SourceFieldOption
} from '../../helpers/articleTypeConfig.types';

import {
  getAllowedFileFormatOptions,
  shouldUseContentLabelMode,
  getValidationFieldDefinitions,
  toFieldFormValues,
  formatKeyPath,
  getDisplayFieldTypeLabel
} from '../../helpers/articleTypeConfig.utils';

import {
  AUTHOR_TYPE_HIDDEN_VALIDATION_KEYS,
  AUTHOR_TYPE_KIND,
  HIDDEN_VALIDATION_KEYS
} from './constants';

import {
  buildFieldEditorSubmit,
  EXTRACTION_SKIP_CHECKBOX_FIELD_NAME,
  normalizeFieldName,
  toFormValues
} from './helpers';
import SourceFieldSection from './Fields/SourceFieldSection';
import RequiredAndDependentSection from './Fields/RequiredAndDependentSection';
import LabelTitleField from './Fields/LabelTitleField';
import LabelSubtitleField from './Fields/LabelSubtitleField';
import LabelContentField from './Fields/LabelContentField';
import HelpTextField from './Fields/HelpTextField';
import ValidationFieldRenderer from './Fields/ValidationFieldRenderer';

type Props = {
  isOpen: boolean;
  mode: 'add' | 'edit';
  defaultValues?: FieldEditorSubmit;
  sourceFieldOptions: SourceFieldOption[];
  mandatoryFieldNames: readonly string[];
  onClose: () => void;
  onSave: (values: FieldEditorSubmit) => void;
};

const ALL_ACCORDION_SECTION_INDICES = [0, 1];
const toValidationRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const FieldEditorModal: React.FC<Props> = ({
  isOpen,
  mode,
  defaultValues,
  sourceFieldOptions,
  mandatoryFieldNames,
  onClose,
  onSave
}) => {
  const initialValues = useMemo(
    () => toFormValues(mode, sourceFieldOptions, defaultValues),
    [defaultValues, mode, sourceFieldOptions]
  );

  const {
    handleSubmit,
    reset,
    setValue,
    setError,
    watch,
    control,
    formState: { errors, isSubmitted, submitCount }
  } = useForm<FieldFormValues>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: initialValues
  });
  const [openAccordionIndices, setOpenAccordionIndices] = useState<number[]>(
    ALL_ACCORDION_SECTION_INDICES
  );

  const selectedSourceFieldName = watch('sourceFieldName');
  const showHelpText = watch('showHelpText');
  const currentDependentField = watch('dependentField');
  const currentFieldName = watch('field');
  const currentKind = watch('kind');
  const watchedValidationValues = watch('validationValues');

  const sourceFieldNames = useMemo(
    () =>
      sourceFieldOptions
        .map(option => option.field)
        .sort((a, b) => a.localeCompare(b)),
    [sourceFieldOptions]
  );

  const selectedSourceField = useMemo(
    () =>
      sourceFieldOptions.find(
        option => option.field === selectedSourceFieldName
      ),
    [selectedSourceFieldName, sourceFieldOptions]
  );

  const mandatoryFieldNameSet = useMemo(
    () => new Set(mandatoryFieldNames.map(normalizeFieldName)),
    [mandatoryFieldNames]
  );

  const activeFieldName = useMemo(() => {
    if (mode === 'add') return selectedSourceField?.field ?? currentFieldName;
    return defaultValues?.field ?? currentFieldName;
  }, [
    currentFieldName,
    defaultValues?.field,
    mode,
    selectedSourceField?.field
  ]);

  const activeLabelSource =
    mode === 'add'
      ? selectedSourceField?.labels ?? {}
      : defaultValues?.labels ?? {};

  const shouldHideTitleAndSubtitle =
    shouldUseContentLabelMode(activeLabelSource);

  const activeValidationSource = useMemo(() => {
    if (mode === 'add') return selectedSourceField?.validations ?? {};
    return defaultValues?.validations ?? {};
  }, [defaultValues?.validations, mode, selectedSourceField?.validations]);

  const activeSourceFieldOption = useMemo(() => {
    if (!activeFieldName) return undefined;

    return sourceFieldOptions.find(
      option =>
        normalizeFieldName(option.field) === normalizeFieldName(activeFieldName)
    );
  }, [activeFieldName, sourceFieldOptions]);

  const responseValidationSource = useMemo(() => {
    if (mode === 'add') return selectedSourceField?.validations ?? {};

    return (
      activeSourceFieldOption?.validations ?? defaultValues?.validations ?? {}
    );
  }, [
    activeSourceFieldOption?.validations,
    defaultValues?.validations,
    mode,
    selectedSourceField?.validations
  ]);

  const activeSourceComponentName = useMemo(
    () => activeSourceFieldOption?.source?.component ?? '',
    [activeSourceFieldOption]
  );

  const displayedTypeName = useMemo(() => {
    return getDisplayFieldTypeLabel({
      fieldName: activeFieldName,
      kind: activeSourceFieldOption?.kind || currentKind,
      componentName: activeSourceComponentName
    });
  }, [
    activeFieldName,
    activeSourceComponentName,
    activeSourceFieldOption?.kind,
    currentKind
  ]);

  const shouldShowValidationFields =
    mode === 'edit' || Boolean(selectedSourceField);

  const isAddModeWithoutSelection = mode === 'add' && !selectedSourceField;
  const isFormDisabled = isAddModeWithoutSelection;

  const normalizedActiveFieldName = normalizeFieldName(activeFieldName || '');
  const isRequiredLockedByMandatoryField =
    mandatoryFieldNameSet.has(normalizedActiveFieldName) &&
    normalizedActiveFieldName !== EXTRACTION_SKIP_CHECKBOX_FIELD_NAME;

  const validationFieldDefinitions = useMemo(
    () => getValidationFieldDefinitions(activeValidationSource),
    [activeValidationSource]
  );

  const responseValidationKeys = useMemo(() => {
    return new Set(Object.keys(toValidationRecord(responseValidationSource)));
  }, [responseValidationSource]);

  const isAuthorTypeKind =
    String(currentKind || '')
      .trim()
      .toLowerCase() === AUTHOR_TYPE_KIND.toLowerCase();

  const visibleValidationFieldDefinitions = useMemo(() => {
    return validationFieldDefinitions.filter((definition: any) => {
      const existsInResponseValidations = responseValidationKeys.has(
        definition.key
      );
      const isHidden = HIDDEN_VALIDATION_KEYS.has(definition.key);
      const isAuthorTypeHidden =
        isAuthorTypeKind &&
        AUTHOR_TYPE_HIDDEN_VALIDATION_KEYS.has(definition.key);

      return existsInResponseValidations && !isHidden && !isAuthorTypeHidden;
    });
  }, [
    isAuthorTypeKind,
    responseValidationKeys,
    validationFieldDefinitions
  ]);

  const allowedFileFormatOptions = useMemo(
    () => getAllowedFileFormatOptions(activeValidationSource),
    [activeValidationSource]
  );

  useEffect(() => {
    if (isOpen) {
      reset(initialValues);
      setOpenAccordionIndices(ALL_ACCORDION_SECTION_INDICES);
    }
  }, [initialValues, isOpen, reset]);

  useEffect(() => {
    const hasErrors = Object.keys(errors || {}).length > 0;

    if (isSubmitted && submitCount > 0 && hasErrors) {
      setOpenAccordionIndices(ALL_ACCORDION_SECTION_INDICES);
    }
  }, [errors, isSubmitted, submitCount]);

  useEffect(() => {
    if (mode !== 'add' || !selectedSourceFieldName || !selectedSourceField) {
      return;
    }

    const nextValues = toFieldFormValues(selectedSourceField);
    const normalizedNextFieldName = normalizeFieldName(nextValues.field);
    const required =
      mandatoryFieldNameSet.has(normalizedNextFieldName) &&
      normalizedNextFieldName !== EXTRACTION_SKIP_CHECKBOX_FIELD_NAME
        ? true
        : nextValues.required;

    const fields: Array<
      [keyof FieldFormValues, FieldFormValues[keyof FieldFormValues]]
    > = [
      ['field', nextValues.field],
      ['kind', nextValues.kind],
      ['required', required],
      ['dependentField', nextValues.dependentField],
      ['validationValues', nextValues.validationValues],
      ['allowedFileFormats', nextValues.allowedFileFormats],
      ['labelTitle', nextValues.labelTitle],
      ['labelSubtitle', nextValues.labelSubtitle],
      ['showHelpText', nextValues.showHelpText],
      ['labelHelpText', nextValues.labelHelpText],
      ['labelContent', nextValues.labelContent]
    ];

    fields.forEach(([key, value]) => {
      setValue(key, value);
    });
  }, [
    mode,
    selectedSourceFieldName,
    selectedSourceField,
    mandatoryFieldNameSet,
    setValue
  ]);

  const onSubmit = (values: FieldFormValues) => {
    if (mode === 'add' && (!selectedSourceField || !values.field.trim())) {
      setError('sourceFieldName', {
        type: 'required',
        message: 'Please select a field'
      });
      return;
    }

    onSave(
      buildFieldEditorSubmit({
        values,
        mode,
        selectedSourceField,
        defaultValues,
        mandatoryFieldNameSet
      })
    );
  };

  const field = watch('field');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width={{ base: 'calc(100vw - 16px)', md: '900px' } as any}
      closeButtonProps={{ onClick: onClose }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex direction="column" gap={4} padding={{ base: 4, md: 8 }}>
          <Flex direction="column" gap={4}>
            <Flex gap={2} alignItems="center">
              <Box>
                <FontAwesomeIcon
                  size="xl"
                  icon={mode === 'add' ? faPlus : faEdit}
                />
              </Box>

              <Text textStyle="headingSection">
                {mode === 'add'
                  ? 'Add Field'
                  : `Edit Field -  ${formatKeyPath(field)}`}
              </Text>
            </Flex>

            <OutlineCard>
              <Box p={3}>
                <Flex direction={mode === 'add' ? 'column' : 'row'} gap={3}>
                  <SourceFieldSection
                    mode={mode}
                    control={control}
                    errors={errors}
                    sourceFieldNames={sourceFieldNames}
                    fieldValue={field}
                  />

                  {field && (
                    <Flex direction="column" gap={2} flex={1} mb={3}>
                      <FieldLabel label="Type" subText="" isRequired={false} />
                      <Text textStyle="textBody">{displayedTypeName}</Text>
                    </Flex>
                  )}
                </Flex>
              </Box>
            </OutlineCard>
          </Flex>

          {field && (
            <Accordion
              chakraAccordionProps={{
                allowMultiple: true,
                allowToggle: true,
                index: openAccordionIndices,
                onChange: (indices: number | number[]) => {
                  setOpenAccordionIndices(
                    Array.isArray(indices)
                      ? indices
                      : indices >= 0
                        ? [indices]
                        : []
                  );
                },
                width: 'full',
                background: 'surfacePrimaryFill'
              }}
              items={[
                {
                  background: 'surfacePrimaryFill',
                  title: <Text textStyle="headingGroup">Labels</Text>,
                  content: (
                    <Box width="100%" p={3}>
                      <Flex direction="column" gap={3} width="100%">
                        {!shouldHideTitleAndSubtitle ? (
                          <LabelTitleField
                            control={control}
                            errors={errors}
                            isFormDisabled={isFormDisabled}
                          />
                        ) : null}

                        {!shouldHideTitleAndSubtitle ? (
                          <LabelSubtitleField
                            control={control}
                            errors={errors}
                            isFormDisabled={isFormDisabled}
                          />
                        ) : null}

                        {shouldHideTitleAndSubtitle ? (
                          <LabelContentField
                            control={control}
                            errors={errors}
                            isFormDisabled={isFormDisabled}
                          />
                        ) : null}

                        <HelpTextField
                          control={control}
                          errors={errors}
                          isFormDisabled={isFormDisabled}
                          showHelpText={Boolean(showHelpText)}
                        />
                      </Flex>
                    </Box>
                  )
                },
                {
                  background: 'surfacePrimaryFill',
                  title: <Text textStyle="headingGroup">Validations</Text>,
                  content: (
                    <Box width="100%" p={3}>
                      <Flex direction="column" gap={3} width="100%">
                        {!shouldShowValidationFields ? (
                          <Text textStyle="textBody" color="gray.600">
                            Select a field to view and edit validation options.
                          </Text>
                        ) : null}

                        <RequiredAndDependentSection
                          control={control}
                          isFormDisabled={isFormDisabled}
                          isMandatoryFieldSelected={
                            isRequiredLockedByMandatoryField
                          }
                          currentDependentField={currentDependentField}
                        />

                        {shouldShowValidationFields ? (
                          <ValidationFieldRenderer
                            definitions={visibleValidationFieldDefinitions}
                            control={control}
                            errors={errors}
                            validationValues={
                              watchedValidationValues as Record<
                                string,
                                unknown
                              >
                            }
                            allowedFileFormatOptions={allowedFileFormatOptions}
                            currentFieldName={currentFieldName}
                            isFormDisabled={isFormDisabled}
                          />
                        ) : null}
                      </Flex>
                    </Box>
                  )
                }
              ]}
            />
          )}
          <Flex justifyContent="flex-end" gap={2} mt={4}>
            <Button variant="secondary" type="button" onClick={onClose}>
              Exit Without Saving
            </Button>
            <Button variant="primary" type="submit">
              {mode === 'add' ? 'Add Field' : 'Save Field'}
            </Button>
          </Flex>
        </Flex>
      </form>
    </Modal>
  );
};

export default FieldEditorModal;
